import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/hooks/useAuth';
import { useWordSuggestions } from '@/hooks/useWordSuggestions';
import { toast } from 'sonner';
import { Plus, AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';

export const WordSuggestionForm = () => {
  const { isAuthenticated } = useAuth();
  const { submitWord, checkWordExists, loading } = useWordSuggestions();
  const [word, setWord] = useState('');
  const [clue, setClue] = useState('');
  const [wordError, setWordError] = useState('');
  const [checking, setChecking] = useState(false);
  const [wordStatus, setWordStatus] = useState<'none' | 'valid' | 'exists' | 'pending' | 'invalid'>('none');

  // Validate word format
  const validateWord = (value: string): boolean => {
    if (!value) return true;
    // Only Swedish letters, no spaces or special characters
    const regex = /^[a-zA-ZåäöÅÄÖ]+$/;
    return regex.test(value);
  };

  // Check word existence with debounce
  useEffect(() => {
    if (!word || word.length < 2) {
      setWordStatus('none');
      setWordError('');
      return;
    }

    if (!validateWord(word)) {
      setWordStatus('invalid');
      setWordError('Endast bokstäver tillåtna (A-Ö)');
      return;
    }

    const timer = setTimeout(async () => {
      setChecking(true);
      const result = await checkWordExists(word);
      setChecking(false);

      if (result.exists) {
        setWordStatus('exists');
        setWordError('Ordet finns redan i databasen');
      } else if (result.pending) {
        setWordStatus('pending');
        setWordError('Ordet väntar redan på granskning');
      } else {
        setWordStatus('valid');
        setWordError('');
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [word, checkWordExists]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!word.trim()) {
      toast.error('Ange ett ord');
      return;
    }

    if (!validateWord(word)) {
      toast.error('Ordet får endast innehålla bokstäver (A-Ö)');
      return;
    }

    if (wordStatus === 'exists' || wordStatus === 'pending') {
      toast.error(wordError);
      return;
    }

    const { error, message } = await submitWord(word.trim(), clue.trim());
    
    if (error) {
      toast.error(error);
    } else {
      toast.success(message || 'Ordet har skickats in!');
      setWord('');
      setClue('');
      setWordStatus('none');
    }
  };

  if (!isAuthenticated) {
    return (
      <Card className="border-dashed">
        <CardContent className="pt-6">
          <div className="text-center text-muted-foreground">
            <Plus className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">
              <Link to="/auth" className="text-primary hover:underline">Logga in</Link>
              {' '}för att föreslå nya ord
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-display flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Föreslå nytt ord
        </CardTitle>
        <CardDescription className="text-xs">
          Hjälp oss utöka orddatabasen med nya ord
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="word" className="text-sm">Ord *</Label>
            <div className="relative">
              <Input
                id="word"
                value={word}
                onChange={(e) => setWord(e.target.value.toUpperCase())}
                placeholder="T.ex. KORSORD"
                maxLength={30}
                className={wordError ? 'border-destructive pr-8' : wordStatus === 'valid' ? 'border-green-500 pr-8' : ''}
              />
              {checking && (
                <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
              )}
              {!checking && wordStatus === 'valid' && (
                <CheckCircle2 className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-green-500" />
              )}
              {!checking && (wordStatus === 'exists' || wordStatus === 'pending' || wordStatus === 'invalid') && (
                <AlertCircle className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-destructive" />
              )}
            </div>
            {wordError && (
              <p className="text-xs text-destructive">{wordError}</p>
            )}
          </div>
          
          <div className="space-y-1.5">
            <Label htmlFor="clue" className="text-sm">Ledtråd (valfritt)</Label>
            <Textarea
              id="clue"
              value={clue}
              onChange={(e) => setClue(e.target.value)}
              placeholder="T.ex. Ordpussel i tidningen"
              maxLength={500}
              rows={2}
              className="resize-none text-sm"
            />
          </div>
          
          <Button 
            type="submit" 
            disabled={loading || wordStatus === 'exists' || wordStatus === 'pending' || wordStatus === 'invalid' || !word}
            className="w-full"
            size="sm"
          >
            {loading ? 'Skickar...' : 'Skicka förslag'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};
